import type { LensCardData } from '~/types/leLens';

import { csv, number, text, type Row } from '../RmMonitor/liveModel';

export const PAGE_ID = 'product-dash';
export const CARD_ID = 'facts';
export const VIEWS = [
  'Performance Daily Summary',
  'Chain Result Summary',
  'Chain Result Comparison',
  'Vendor Result Summary',
  'Vendor Details',
  'Vendor Result Comparison',
  'Destination Result Summary',
] as const;
export type ViewId = (typeof VIEWS)[number];

export const PERFORMANCE_WINDOW = { from: '2023-07-01', to: '2024-02-08' };
const HIERARCHY_START = '2015-01-01';

/** Today as a Sydney calendar date (YYYY-MM-DD). */
export function sydneyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(now);
}
// Dropdown order and labels. The default comparison stays 'month' (see ProductDashPage).
export const COMPARISONS = [
  ['week', 'Previous Week'],
  ['month', 'Previous Month'],
  ['quarter', 'Previous Quarter'],
  ['year', 'Previous Year'],
] as const;
export const WEEKDAYS = ['Friday', 'Monday', 'Saturday', 'Sunday', 'Thursday', 'Tuesday', 'Wednesday'];
export const LEAD_RANGES = [
  '1 day',
  '2 to 10 days',
  '11 to 30 days',
  '31 to 60 days',
  '61 to 120 days',
  '121 to 180 days',
  'More than 180 days',
];
export const ADR_RANGES = [
  'Less than $100',
  'Between $101 and $200',
  'Between $201 and $300',
  'Between $301 and $400',
  'Between $401 and $500',
  'Between $501 and $1000',
  'Between $1001 and $2000',
  'Between $2001 and $5000',
  'Between $5001 and $10000',
  'More than $10001',
];

export type Measures = {
  grossCp: number | null;
  grossPp: number | null;
  netCp: number | null;
  netPp: number | null;
  marginCp: number | null;
  marginPp: number | null;
  grossAllCp: number | null;
  grossAllPp: number | null;
  gmArCp: number | null;
  gmArPp: number | null;
  vendorCp: number | null;
  vendorPp: number | null;
  nightsCp: number | null;
  nightsPp: number | null;
  packagesCp: number | null;
  packagesPp: number | null;
  leadSumCp: number | null;
  leadNCp: number | null;
  leadSumPp: number | null;
  leadNPp: number | null;
  npCp: number | null;
  npPp: number | null;
  costCp: number | null;
  costPp: number | null;
  saleOrdersCp: number | null;
  saleOrdersPp: number | null;
  flightCp: number | null;
  flightPp: number | null;
  insuranceCp: number | null;
  insurancePp: number | null;
  ordersCp: number | null;
  ordersPp: number | null;
};

export type Fact = Measures & {
  grain: string;
  txnDate: string | null;
  label: string | null;
  label2: string | null;
  batch: number | null;
  payload: string | null;
};

export type VendorFact = Measures & { vendor: string; chain: string | null };

const MEASURE_KEYS: Array<[keyof Measures, string]> = [
  ['grossCp', 'gross_cp'],
  ['grossPp', 'gross_pp'],
  ['netCp', 'net_cp'],
  ['netPp', 'net_pp'],
  ['marginCp', 'margin_cp'],
  ['marginPp', 'margin_pp'],
  ['grossAllCp', 'gross_all_cp'],
  ['grossAllPp', 'gross_all_pp'],
  ['gmArCp', 'gm_ar_cp'],
  ['gmArPp', 'gm_ar_pp'],
  ['vendorCp', 'vendor_cp'],
  ['vendorPp', 'vendor_pp'],
  ['nightsCp', 'nights_cp'],
  ['nightsPp', 'nights_pp'],
  ['packagesCp', 'packages_cp'],
  ['packagesPp', 'packages_pp'],
  ['leadSumCp', 'lead_sum_cp'],
  ['leadNCp', 'lead_n_cp'],
  ['leadSumPp', 'lead_sum_pp'],
  ['leadNPp', 'lead_n_pp'],
  ['npCp', 'np_cp'],
  ['npPp', 'np_pp'],
  ['costCp', 'cost_cp'],
  ['costPp', 'cost_pp'],
  ['saleOrdersCp', 'sale_orders_cp'],
  ['saleOrdersPp', 'sale_orders_pp'],
  ['flightCp', 'flight_cp'],
  ['flightPp', 'flight_pp'],
  ['insuranceCp', 'insurance_cp'],
  ['insurancePp', 'insurance_pp'],
  ['ordersCp', 'orders_cp'],
  ['ordersPp', 'orders_pp'],
];

function cell(value: unknown): unknown {
  return value && typeof value === 'object' && 'value' in value ? (value as { value: unknown }).value : value;
}

function measuresOf(row: Record<string, unknown>): Measures {
  return Object.fromEntries(MEASURE_KEYS.map(([key, column]) => [key, number(cell(row[column]))])) as Measures;
}

/** Chain, vendor and destination views run to today, matching the Tableau workbook's full-range date filter. */
export function windowFor(view: string, now = new Date()) {
  return view === 'Performance Daily Summary' ? PERFORMANCE_WINDOW : { from: HIERARCHY_START, to: sydneyToday(now) };
}

export function readFacts(cards: ReadonlyArray<LensCardData>): Fact[] {
  const card = cards.find((entry) => entry.cardId === CARD_ID);
  return (card?.rows ?? []).map((row) => ({
    ...measuresOf(row),
    grain: text(cell(row.grain)) === 'Unknown' ? '' : text(cell(row.grain)),
    txnDate: cell(row.txn_date) == null ? null : String(cell(row.txn_date)).slice(0, 10),
    label: cell(row.label) == null ? null : String(cell(row.label)),
    label2: cell(row.label2) == null ? null : String(cell(row.label2)),
    batch: number(cell(row.batch)),
    payload: cell(row.payload) == null ? null : String(cell(row.payload)),
  }));
}

export function byGrain(facts: Fact[], grain: string) {
  return facts.filter((fact) => fact.grain === grain);
}

export function vendorsOf(facts: Fact[]): VendorFact[] {
  const parsed: VendorFact[] = [];
  for (const fact of byGrain(facts, 'vendor')) {
    if (!fact.payload) continue;
    const rows = JSON.parse(fact.payload) as Array<Record<string, unknown>>;
    for (const row of rows) parsed.push({ ...measuresOf(row), vendor: String(row.vendor ?? ''), chain: row.chain == null ? null : String(row.chain) });
  }
  return sortByGross(parsed, (row) => row.grossCp, (row) => row.vendor);
}

export function sortByGross<T>(rows: T[], gross: (row: T) => number | null, label: (row: T) => string): T[] {
  return [...rows].sort((a, b) => (gross(b) ?? Number.NEGATIVE_INFINITY) - (gross(a) ?? Number.NEGATIVE_INFINITY) || label(a).localeCompare(label(b)));
}

export function change(current: number | null, prior: number | null): number | null {
  if (current === null || prior === null || prior === 0) return null;
  return current / prior - 1;
}

export function ratio(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator === 0) return null;
  return numerator / denominator;
}

export function lead(sum: number | null, count: number | null) {
  return ratio(sum, count);
}

export function money(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return '';
  const rounded = digits === 0 ? Math.round(value) : value;
  const body = Math.abs(rounded).toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return value < 0 ? `-$${body}` : `$${body}`;
}

export function integer(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return Math.round(value).toLocaleString('en-AU');
}

export function percent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return `${(value * 100).toFixed(2)}%`;
}

export function decimal(value: number | null, digits: number): string {
  if (value === null || !Number.isFinite(value)) return '';
  return value.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function movement(value: number | null): 'Up' | 'Down' | '' {
  if (value === null) return '';
  return value > 0 ? 'Up' : 'Down';
}

export type Headline = {
  label: string;
  value: string;
  change: string;
  movement: 'Up' | 'Down' | '';
  series: number[];
};

export function headlines(total: Measures | undefined, days: Fact[]): Headline[] {
  const point = (pick: (day: Fact) => number | null) => days.map((day) => pick(day) ?? 0);
  const tile = (
    label: string,
    current: number | null,
    prior: number | null,
    format: (value: number | null) => string,
    series: number[],
  ): Headline => {
    const delta = change(current, prior);
    return { label, value: format(current), change: percent(delta), movement: movement(delta), series };
  };
  const row = total;
  return [
    tile('Gross Sales', row?.grossCp ?? null, row?.grossPp ?? null, (value) => money(value), point((day) => day.grossCp)),
    tile('Net Sales', row?.netCp ?? null, row?.netPp ?? null, (value) => money(value), point((day) => day.netCp)),
    tile(
      'Gross Margin %',
      ratio(row?.marginCp ?? null, row?.grossAllCp ?? null),
      ratio(row?.marginPp ?? null, row?.grossAllPp ?? null),
      percent,
      point((day) => ratio(day.marginCp, day.grossAllCp)),
    ),
    tile('Vendor Revenue', row?.vendorCp ?? null, row?.vendorPp ?? null, (value) => money(value), point((day) => day.vendorCp)),
    tile('Room Nights', row?.nightsCp ?? null, row?.nightsPp ?? null, integer, point((day) => day.nightsCp)),
    tile('Avg. Leadtime', lead(row?.leadSumCp ?? null, row?.leadNCp ?? null), lead(row?.leadSumPp ?? null, row?.leadNPp ?? null), (value) => decimal(value, 1), point((day) => lead(day.leadSumCp, day.leadNCp))),
    tile('Total Package Sold', row?.packagesCp ?? null, row?.packagesPp ?? null, integer, point((day) => day.packagesCp)),
    tile('ALOS', ratio(row?.npCp ?? null, row?.packagesCp ?? null), ratio(row?.npPp ?? null, row?.packagesPp ?? null), (value) => decimal(value, 3), point((day) => ratio(day.npCp, day.packagesCp))),
    tile('Net ADR', ratio(row?.costCp ?? null, row?.nightsCp ?? null), ratio(row?.costPp ?? null, row?.nightsPp ?? null), (value) => money(value), point((day) => ratio(day.costCp, day.nightsCp))),
    tile(
      'AOV',
      ratio(row?.grossAllCp ?? null, row?.saleOrdersCp ?? null),
      ratio(row?.grossAllPp ?? null, row?.saleOrdersPp ?? null),
      (value) => money(value),
      point((day) => ratio(day.grossAllCp, day.saleOrdersCp)),
    ),
  ];
}

export const CHAIN_COLUMNS = [
  'Chain Name',
  'Gross Sales AUD',
  'Net Sales',
  'GM (AR)',
  'Gross Margin %',
  'Vendor Revenue (AR)',
  'Room Nights',
  'Package Sold',
  'Avg. Leadtime',
  'Avg. LOS',
  'Net ADR',
  'Attach Rate Flight',
  'Attach Rate Insurance',
] as const;

export function chainCells(row: Measures, compare: boolean): string[] {
  const gm = (margin: number | null, gross: number | null) => percent(ratio(margin, gross));
  const current = [
    money(row.grossCp),
    money(row.netCp),
    money(row.gmArCp),
    gm(row.marginCp, row.grossAllCp),
    money(row.vendorCp),
    integer(row.nightsCp),
    integer(row.packagesCp),
    decimal(lead(row.leadSumCp, row.leadNCp), 0),
    decimal(ratio(row.npCp, row.packagesCp), 2),
    money(ratio(row.costCp, row.nightsCp)),
    percent(ratio(row.flightCp, row.ordersCp)),
    percent(ratio(row.insuranceCp, row.ordersCp)),
  ];
  if (!compare) return current;
  return current.flatMap((value, index) => {
    const priors = [
      row.grossPp,
      row.netPp,
      row.gmArPp,
      ratio(row.marginPp, row.grossAllPp),
      row.vendorPp,
      row.nightsPp,
      row.packagesPp,
      lead(row.leadSumPp, row.leadNPp),
      ratio(row.npPp, row.packagesPp),
      ratio(row.costPp, row.nightsPp),
      ratio(row.flightPp, row.ordersPp),
      ratio(row.insurancePp, row.ordersPp),
    ];
    const currents = [
      row.grossCp,
      row.netCp,
      row.gmArCp,
      ratio(row.marginCp, row.grossAllCp),
      row.vendorCp,
      row.nightsCp,
      row.packagesCp,
      lead(row.leadSumCp, row.leadNCp),
      ratio(row.npCp, row.packagesCp),
      ratio(row.costCp, row.nightsCp),
      ratio(row.flightCp, row.ordersCp),
      ratio(row.insuranceCp, row.ordersCp),
    ];
    return [value, percent(change(currents[index], priors[index]))];
  });
}

export function performanceCsv(days: Fact[]) {
  const rows = [...days].sort((a, b) => (a.txnDate ?? '').localeCompare(b.txnDate ?? ''));
  return csv(
    rows.map((day) => ({
      Date: day.txnDate ?? '',
      'Gross Sales': day.grossCp ?? 0,
      'Net Sales': day.netCp ?? 0,
      'Room Nights': day.nightsCp ?? 0,
      'Package Sold': day.packagesCp ?? 0,
    })),
    ['Date', 'Gross Sales', 'Net Sales', 'Room Nights', 'Package Sold'],
  );
}

export function hierarchyCsv(rows: Array<Measures & { name: string; extra?: string }>, name: string) {
  return csv(
    rows.map((row) => ({
      [name]: row.name,
      ...(row.extra === undefined ? {} : { Country: row.extra }),
      'Gross Sales': row.grossCp ?? 0,
      'Net Sales': row.netCp ?? 0,
      'GM (AR)': row.gmArCp ?? 0,
      'Room Nights': row.nightsCp ?? 0,
      'Package Sold': row.packagesCp ?? 0,
    })),
    [name, ...(rows.some((row) => row.extra !== undefined) ? ['Country'] : []), 'Gross Sales', 'Net Sales', 'GM (AR)', 'Room Nights', 'Package Sold'],
  );
}

export function checkinGrid(facts: Fact[]) {
  const months = [...new Set(byGrain(facts, 'checkin').map((fact) => fact.label2).filter((value): value is string => Boolean(value)))].sort();
  const cells = new Map<string, number>();
  for (const fact of byGrain(facts, 'checkin')) cells.set(`${fact.label2}|${fact.label}`, fact.packagesCp ?? 0);
  return { months, weekdays: WEEKDAYS, value: (month: string, weekday: string) => cells.get(`${month}|${weekday}`) ?? 0 };
}

export function matrixGrid(facts: Fact[]) {
  const cells = new Map<string, number>();
  for (const fact of byGrain(facts, 'matrix')) cells.set(`${fact.label}|${fact.label2}`, fact.packagesCp ?? 0);
  return { leads: LEAD_RANGES, adrs: ADR_RANGES, value: (leadRange: string, adrRange: string) => cells.get(`${leadRange}|${adrRange}`) ?? 0 };
}

export function ranked(rows: Fact[], limit: number) {
  return sortByGross(byGrain(rows, 'chain').length ? byGrain(rows, 'chain') : rows, (row) => row.grossCp, (row) => row.label ?? '').slice(0, limit);
}

export { csv, type Row };
